import React, { useCallback, useEffect, useState } from 'react';
import { getUserAccessToken, getUserSession, persistUserSession } from '../../../services/userSession';
import { resolveScreenHostContext } from '../../../services/resolveScreenHostContext';
import {
    getScreenHostTeam,
    inviteScreenHostTeamMember,
    updateScreenHostTeamMember,
    revokeScreenHostTeamMember,
} from '../../../services/screenHostTeam';
import './ScreenHostTeamManagement.css';

function normalizePhoneInput(value) {
    return String(value || '').replace(/\D/g, '').replace(/^1/, '').slice(0, 10);
}

export default function ScreenHostTeamManagement() {
    const token = getUserAccessToken();
    const { ownerUserId } = resolveScreenHostContext(getUserSession() || {}, token);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [invitePhone, setInvitePhone] = useState('');
    const [inviteRole, setInviteRole] = useState('editor');
    const [inviteFirstName, setInviteFirstName] = useState('');
    const [inviteLastName, setInviteLastName] = useState('');
    const [inviting, setInviting] = useState(false);
    const [actionMemberId, setActionMemberId] = useState(null);

    const loadMembers = useCallback(async () => {
        if (!token || !ownerUserId) {
            return;
        }
        setLoading(true);
        setError('');
        try {
            const { data } = await getScreenHostTeam(token, ownerUserId);
            setMembers(Array.isArray(data?.members) ? data.members : []);
        } catch (err) {
            setError(err?.response?.data?.error || 'Could not load team members');
        } finally {
            setLoading(false);
        }
    }, [token, ownerUserId]);

    useEffect(() => {
        loadMembers();
    }, [loadMembers]);

    async function handleInvite(event) {
        event.preventDefault();
        const phoneNumber = normalizePhoneInput(invitePhone);
        if (phoneNumber.length < 10) {
            setError('Enter a valid 10-digit phone number');
            return;
        }

        setInviting(true);
        setError('');
        try {
            await inviteScreenHostTeamMember(token, {
                userId: ownerUserId,
                phoneNumber,
                role: inviteRole,
                firstName: inviteFirstName.trim() || undefined,
                lastName: inviteLastName.trim() || undefined,
            });
            setInvitePhone('');
            setInviteFirstName('');
            setInviteLastName('');
            await loadMembers();
        } catch (err) {
            setError(err?.response?.data?.error || 'Could not invite team member');
        } finally {
            setInviting(false);
        }
    }

    async function handleRoleChange(member, nextRole) {
        if (!member.isActive || member.role === nextRole) {
            return;
        }
        setActionMemberId(member.hostTeamMemberId);
        setError('');
        try {
            await updateScreenHostTeamMember(token, member.hostTeamMemberId, {
                userId: ownerUserId,
                role: nextRole,
            });
            await loadMembers();
        } catch (err) {
            setError(err?.response?.data?.error || 'Could not update role');
        } finally {
            setActionMemberId(null);
        }
    }

    async function handleRevoke(member) {
        if (!member.isActive) {
            return;
        }
        const confirmed = window.confirm(`Remove ${member.firstName || ''} ${member.lastName || ''} from your team?`);
        if (!confirmed) {
            return;
        }
        setActionMemberId(member.hostTeamMemberId);
        setError('');
        try {
            await revokeScreenHostTeamMember(token, member.hostTeamMemberId, ownerUserId);
            await loadMembers();
        } catch (err) {
            setError(err?.response?.data?.error || 'Could not remove team member');
        } finally {
            setActionMemberId(null);
        }
    }

    return (
        <div className="screenHostTeamPage">
            <h1 className="screenHostTeamTitle">Team access</h1>
            <p className="screenHostTeamSubtitle">
                Invite people to log in with their own phone number and manage your screens.
            </p>

            <form className="screenHostTeamInviteForm" onSubmit={handleInvite}>
                <h2>Add team member</h2>
                <div className="screenHostTeamFormRow">
                    <label>
                        Phone number
                        <input
                            type="tel"
                            value={invitePhone}
                            onChange={(e) => setInvitePhone(e.target.value)}
                            placeholder="5551234567"
                            required
                        />
                    </label>
                    <label>
                        Role
                        <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                            <option value="editor">Editor — content only</option>
                            <option value="admin">Admin — content and screen settings</option>
                        </select>
                    </label>
                </div>
                <div className="screenHostTeamFormRow">
                    <label>
                        First name (optional)
                        <input
                            type="text"
                            value={inviteFirstName}
                            onChange={(e) => setInviteFirstName(e.target.value)}
                        />
                    </label>
                    <label>
                        Last name (optional)
                        <input
                            type="text"
                            value={inviteLastName}
                            onChange={(e) => setInviteLastName(e.target.value)}
                        />
                    </label>
                </div>
                <button type="submit" disabled={inviting}>
                    {inviting ? 'Sending invite…' : 'Send invite'}
                </button>
            </form>

            {error ? <p className="screenHostTeamError">{error}</p> : null}

            <section className="screenHostTeamListSection">
                <h2>Team members</h2>
                {loading ? <p>Loading…</p> : null}
                {!loading && members.length === 0 ? <p>No team members yet.</p> : null}
                {!loading && members.length > 0 ? (
                    <ul className="screenHostTeamList">
                        {members.map((member) => (
                            <li key={member.hostTeamMemberId} className={member.isActive ? '' : 'screenHostTeamListItem--revoked'}>
                                <div>
                                    <strong>{[member.firstName, member.lastName].filter(Boolean).join(' ') || 'Team member'}</strong>
                                    <div>{member.phoneNumber || member.email || 'No contact info'}</div>
                                    {!member.isActive ? <span className="screenHostTeamBadge">Revoked</span> : null}
                                </div>
                                <div className="screenHostTeamListActions">
                                    <select
                                        value={member.role}
                                        disabled={!member.isActive || actionMemberId === member.hostTeamMemberId}
                                        onChange={(e) => handleRoleChange(member, e.target.value)}
                                    >
                                        <option value="editor">Editor</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                    {member.isActive ? (
                                        <button
                                            type="button"
                                            disabled={actionMemberId === member.hostTeamMemberId}
                                            onClick={() => handleRevoke(member)}
                                        >
                                            Remove
                                        </button>
                                    ) : null}
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : null}
            </section>
        </div>
    );
}
